import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { orchestratorReducer, initialOrchestratorState } from './reducer';
const OrchestratorContext = /*#__PURE__*/createContext(null);
const isDev = typeof process !== 'undefined' ? process.env.NODE_ENV !== 'production' : true;
function isPromise(value) {
  return typeof value === 'object' && value !== null && 'then' in value;
}
function findStepAtOrBefore(stepIndexes, flowIndex) {
  let left = 0;
  let right = stepIndexes.length - 1;
  let stepFlowIndex = -1;
  let stepIndex = -1;
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const candidate = stepIndexes[mid];
    if (candidate <= flowIndex) {
      stepFlowIndex = candidate;
      stepIndex = mid;
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }
  return {
    stepFlowIndex,
    stepIndex
  };
}
function useOrchestratorFlow(flow, onError, loadingComponent, initialIndex, initialStepId, onStepChange, onStepChangeDetailed, onNavigate) {
  const flowInfo = useMemo(() => {
    const stepIndexes = [];
    const idToIndex = new Map();
    const duplicateIds = new Set();
    flow.forEach((item, index) => {
      if (idToIndex.has(item.id)) {
        duplicateIds.add(item.id);
      } else {
        idToIndex.set(item.id, index);
      }
      if (item.type === 'step') {
        stepIndexes.push(index);
      }
    });
    return {
      stepIndexes,
      idToIndex,
      duplicateIds,
      flowLength: flow.length
    };
  }, [flow]);
  const resolveInitialIndex = useCallback(() => {
    if (flowInfo.flowLength === 0) {
      return 0;
    }
    if (initialIndex !== undefined) {
      return Math.max(0, Math.min(flowInfo.flowLength - 1, initialIndex));
    }
    if (initialStepId) {
      const stepIndex = flowInfo.idToIndex.get(initialStepId);
      if (stepIndex !== undefined) {
        return stepIndex;
      }
    }
    return 0;
  }, [flowInfo.flowLength, flowInfo.idToIndex, initialIndex, initialStepId]);
  const [state, dispatch] = useReducer(orchestratorReducer, {
    ...initialOrchestratorState,
    currentIndex: resolveInitialIndex()
  });
  const lastExecutedRef = useRef(null);
  const lastIndexRef = useRef(state.currentIndex);
  const lastNavigationReasonRef = useRef('init');
  const latestIndexRef = useRef(state.currentIndex);
  latestIndexRef.current = state.currentIndex;
  const executionIdRef = useRef(0);
  const stepActiveRef = useRef(false);
  useEffect(() => {
    executionIdRef.current += 1;
  }, [state.currentIndex, state.navigationContext.skipped]);
  useEffect(() => {
    if (state.currentIndex >= flowInfo.flowLength && flowInfo.flowLength > 0) {
      dispatch({
        type: 'GO_TO_INDEX',
        payload: flowInfo.flowLength - 1
      });
    }
  }, [flowInfo.flowLength, state.currentIndex]);
  useEffect(() => {
    if (!isDev) {
      return;
    }
    if (flowInfo.flowLength === 0) {
      console.warn('Orchestrator: flow is empty.');
    }
    if (flowInfo.duplicateIds.size > 0) {
      console.warn(`Orchestrator: duplicate ids found (${Array.from(flowInfo.duplicateIds).join(', ')}).`);
    }
  }, [flowInfo.flowLength, flowInfo.duplicateIds]);
  const getCurrentItem = useCallback(() => {
    return flow[state.currentIndex];
  }, [flow, state.currentIndex]);
  const getStepAtOrBefore = useCallback(flowIndex => {
    return findStepAtOrBefore(flowInfo.stepIndexes, flowIndex);
  }, [flowInfo.stepIndexes]);
  const getCurrentStep = useCallback(() => {
    const {
      stepFlowIndex
    } = getStepAtOrBefore(state.currentIndex);
    if (stepFlowIndex < 0) {
      return null;
    }
    const item = flow[stepFlowIndex];
    if ((item == null ? void 0 : item.type) !== 'step') {
      return null;
    }
    return {
      id: item.id,
      component: item.component,
      params: item.params,
      meta: item.meta
    };
  }, [flow, getStepAtOrBefore, state.currentIndex]);
  const getCurrentStepFlowIndex = useCallback(() => {
    return getStepAtOrBefore(state.currentIndex).stepFlowIndex;
  }, [getStepAtOrBefore, state.currentIndex]);
  const getCurrentStepIndex = useCallback(() => {
    const {
      stepIndex
    } = getStepAtOrBefore(state.currentIndex);
    return Math.max(0, stepIndex);
  }, [getStepAtOrBefore, state.currentIndex]);
  const buildNavigationContext = useCallback((currentIndex, itemOverride) => {
    const item = itemOverride ?? flow[currentIndex];
    const {
      stepIndex
    } = getStepAtOrBefore(currentIndex);
    const safeStepIndex = Math.max(0, stepIndex);
    const totalSteps = flowInfo.stepIndexes.length;
    const isFirstStep = totalSteps > 0 ? safeStepIndex <= 0 : true;
    const isLastStep = totalSteps > 0 ? safeStepIndex >= totalSteps - 1 : true;
    const isComplete = flowInfo.flowLength === 0 ? false : currentIndex >= flowInfo.flowLength - 1;
    const context = {
      skipped: state.navigationContext.skipped,
      currentIndex,
      currentItemId: item == null ? void 0 : item.id,
      currentItemType: item == null ? void 0 : item.type,
      stepIndex: safeStepIndex,
      totalSteps,
      flowLength: flowInfo.flowLength,
      isComplete,
      isFirstStep,
      isLastStep
    };
    return context;
  }, [flow, flowInfo.flowLength, flowInfo.stepIndexes.length, getStepAtOrBefore, state.navigationContext.skipped]);
  const findPreviousStepIndex = useCallback(startIndex => {
    const {
      stepIndexes
    } = flowInfo;
    if (!stepIndexes.length) {
      return -1;
    }
    const {
      stepIndex: currentStepIndex
    } = getStepAtOrBefore(startIndex);
    for (let i = currentStepIndex - 1; i >= 0; i--) {
      const stepFlowIndex = stepIndexes[i];
      const item = flow[stepFlowIndex];
      if (!item || item.type !== 'step') {
        continue;
      }
      return stepFlowIndex;
    }
    return -1;
  }, [flow, flowInfo, getStepAtOrBefore]);
  const canGoBack = useCallback(() => {
    if (state.isLoading) {
      return false;
    }
    const currentStepIndex = getCurrentStepFlowIndex();
    if (currentStepIndex <= 0) {
      return false;
    }
    return findPreviousStepIndex(currentStepIndex) >= 0;
  }, [findPreviousStepIndex, getCurrentStepFlowIndex, state.isLoading]);
  const canGoNext = useCallback(() => {
    if (state.isLoading) {
      return false;
    }
    return state.currentIndex < flowInfo.flowLength - 1;
  }, [flowInfo.flowLength, state.currentIndex, state.isLoading]);
  const isComplete = useCallback(() => {
    if (state.isLoading) {
      return false;
    }
    return flowInfo.flowLength > 0 && state.currentIndex >= flowInfo.flowLength - 1;
  }, [flowInfo.flowLength, state.currentIndex, state.isLoading]);
  const executeCurrentItem = useCallback(async () => {
    const currentItem = getCurrentItem();
    if (!currentItem) {
      return;
    }
    const signature = `${state.currentIndex}:${state.navigationContext.skipped}:${currentItem.id}`;
    if (lastExecutedRef.current === signature && (currentItem.type === 'action' || currentItem.type === 'step' || currentItem.type === 'task')) {
      return;
    }
    lastExecutedRef.current = signature;
    const navigationContext = buildNavigationContext(state.currentIndex, currentItem);
    try {
      switch (currentItem.type) {
        case 'step':
          {
            if (currentItem.shouldSkip && lastNavigationReasonRef.current !== 'back') {
              const shouldSkipResult = currentItem.shouldSkip(navigationContext);
              const shouldSkip = isPromise(shouldSkipResult) ? await shouldSkipResult : shouldSkipResult;
              if (shouldSkip) {
                stepActiveRef.current = false;
                lastNavigationReasonRef.current = 'skip';
                dispatch({
                  type: 'SKIP'
                });
                return;
              }
            }
            stepActiveRef.current = true;
            break;
          }
        case 'task':
          {
            const executionId = executionIdRef.current;
            const maybePromise = currentItem.handler(navigationContext);
            if (isPromise(maybePromise)) {
              await maybePromise;
            }
            if (executionId !== executionIdRef.current) {
              return;
            }
            lastNavigationReasonRef.current = 'next';
            dispatch({
              type: 'NEXT'
            });
            break;
          }
        case 'action':
          {
            dispatch({
              type: 'SET_LOADING',
              payload: true
            });
            const executionId = executionIdRef.current;
            await currentItem.handler(navigationContext);
            if (executionId !== executionIdRef.current) {
              dispatch({
                type: 'SET_LOADING',
                payload: false
              });
              return;
            }
            dispatch({
              type: 'SET_LOADING',
              payload: false
            });
            lastNavigationReasonRef.current = 'next';
            dispatch({
              type: 'NEXT'
            });
            break;
          }
      }
    } catch (error) {
      if (currentItem.type === 'action') {
        dispatch({
          type: 'SET_LOADING',
          payload: false
        });
      }
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorContext = {
        itemId: currentItem.id,
        itemType: currentItem.type,
        flowIndex: state.currentIndex,
        stepIndex: getCurrentStepIndex(),
        totalSteps: flowInfo.stepIndexes.length
      };
      if (onError) {
        onError(errorMessage, currentItem.id, errorContext);
      }
      if (currentItem.type === 'action' || currentItem.type === 'task') {
        const previousStepIndex = findPreviousStepIndex(state.currentIndex);
        lastNavigationReasonRef.current = 'back';
        dispatch({
          type: 'GO_TO_INDEX',
          payload: previousStepIndex >= 0 ? previousStepIndex : 0
        });
      }
    }
  }, [buildNavigationContext, findPreviousStepIndex, flowInfo.stepIndexes.length, getCurrentItem, getCurrentStepIndex, onError, state.currentIndex, state.navigationContext.skipped]);
  useEffect(() => {
    executeCurrentItem();
  }, [executeCurrentItem]);
  const goToIndex = useCallback(targetIndex => {
    if (state.isLoading) {
      return;
    }
    if (flowInfo.flowLength === 0) {
      return;
    }
    const clampedIndex = Math.max(0, Math.min(flowInfo.flowLength - 1, targetIndex));
    if (clampedIndex === state.currentIndex) {
      return;
    }
    stepActiveRef.current = false;
    lastNavigationReasonRef.current = 'goToIndex';
    dispatch({
      type: 'GO_TO_INDEX',
      payload: clampedIndex
    });
  }, [flowInfo.flowLength, state.currentIndex, state.isLoading]);
  const next = useCallback(async () => {
    if (state.isLoading) {
      return;
    }
    if (state.currentIndex >= flowInfo.flowLength - 1) {
      return;
    }
    // Fix: Block stale calls where the closure's index doesn't match the current authoritative index
    if (state.currentIndex !== latestIndexRef.current) {
      return;
    }
    stepActiveRef.current = false;
    lastNavigationReasonRef.current = 'next';
    dispatch({
      type: 'NEXT'
    });
  }, [flowInfo.flowLength, state.currentIndex, state.isLoading]);
  const skip = useCallback(async () => {
    if (state.isLoading) {
      return;
    }
    if (state.currentIndex >= flowInfo.flowLength - 1) {
      return;
    }
    if (!stepActiveRef.current) {
      return;
    }
    stepActiveRef.current = false;
    lastNavigationReasonRef.current = 'skip';
    dispatch({
      type: 'SKIP'
    });
  }, [flowInfo.flowLength, state.currentIndex, state.isLoading]);
  const back = useCallback(() => {
    if (state.isLoading) {
      return;
    }
    if (!canGoBack()) {
      return;
    }
    const currentStepIndex = getCurrentStepFlowIndex();
    const previousStepIndex = findPreviousStepIndex(currentStepIndex);
    if (previousStepIndex >= 0) {
      stepActiveRef.current = false;
      lastNavigationReasonRef.current = 'back';
      dispatch({
        type: 'GO_TO_INDEX',
        payload: previousStepIndex
      });
    }
  }, [canGoBack, findPreviousStepIndex, getCurrentStepFlowIndex, state.isLoading]);
  const navigate = useCallback(targetId => {
    if (state.isLoading) {
      return;
    }
    const targetIndex = flowInfo.idToIndex.get(targetId);
    if (targetIndex === undefined || targetIndex === state.currentIndex) {
      return;
    }
    stepActiveRef.current = false;
    lastNavigationReasonRef.current = 'navigate';
    dispatch({
      type: 'GO_TO_INDEX',
      payload: targetIndex
    });
  }, [flowInfo.idToIndex, state.currentIndex, state.isLoading]);
  const goToStepId = useCallback(targetId => {
    const targetIndex = flowInfo.idToIndex.get(targetId);
    if (targetIndex === undefined) {
      return;
    }
    const item = flow[targetIndex];
    if ((item == null ? void 0 : item.type) !== 'step') {
      return;
    }
    goToIndex(targetIndex);
  }, [flow, flowInfo.idToIndex, goToIndex]);
  const goToStepIndex = useCallback(stepIndex => {
    if (stepIndex < 0 || stepIndex >= flowInfo.stepIndexes.length) {
      return;
    }
    const targetIndex = flowInfo.stepIndexes[stepIndex];
    goToIndex(targetIndex);
  }, [flowInfo.stepIndexes, goToIndex]);
  const reset = useCallback(() => {
    stepActiveRef.current = false;
    lastNavigationReasonRef.current = 'reset';
    dispatch({
      type: 'RESET',
      payload: resolveInitialIndex()
    });
  }, [resolveInitialIndex]);
  const currentStep = getCurrentStep();
  const currentStepIndex = getCurrentStepIndex();
  const totalSteps = flowInfo.stepIndexes.length;
  const isFirstStep = totalSteps > 0 ? currentStepIndex <= 0 : true;
  const isLastStep = totalSteps > 0 ? currentStepIndex >= totalSteps - 1 : true;
  const progress = totalSteps <= 1 ? currentStep ? 1 : 0 : currentStepIndex / (totalSteps - 1);
  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep);
    }
    if (onStepChangeDetailed) {
      onStepChangeDetailed({
        step: currentStep,
        stepIndex: currentStepIndex,
        totalSteps,
        flowIndex: state.currentIndex
      });
    }
    if (currentStep) {
      stepActiveRef.current = true;
    }
  }, [currentStep, currentStepIndex, onStepChange, onStepChangeDetailed, state.currentIndex, totalSteps]);
  const emitNavigate = useCallback((reason, fromIndex, toIndex) => {
    if (!onNavigate) {
      return;
    }
    const fromItem = flow[fromIndex];
    const toItem = flow[toIndex];
    const stepIndex = getStepAtOrBefore(toIndex).stepIndex;
    onNavigate({
      fromIndex,
      toIndex,
      reason,
      fromId: fromItem == null ? void 0 : fromItem.id,
      toId: toItem == null ? void 0 : toItem.id,
      fromType: fromItem == null ? void 0 : fromItem.type,
      toType: toItem == null ? void 0 : toItem.type,
      stepIndex: Math.max(0, stepIndex),
      totalSteps
    });
  }, [flow, getStepAtOrBefore, onNavigate, totalSteps]);
  useEffect(() => {
    if (!onNavigate) {
      return;
    }
    if (lastIndexRef.current !== state.currentIndex) {
      emitNavigate(lastNavigationReasonRef.current, lastIndexRef.current, state.currentIndex);
      lastIndexRef.current = state.currentIndex;
    }
  }, [emitNavigate, onNavigate, state.currentIndex]);
  return useMemo(() => ({
    currentStep,
    currentParams: currentStep == null ? void 0 : currentStep.params,
    isLoading: state.isLoading,
    currentFlowIndex: state.currentIndex,
    currentStepIndex,
    totalSteps,
    isComplete: isComplete(),
    isFirstStep,
    isLastStep,
    progress,
    canGoBack: canGoBack(),
    canGoNext: canGoNext(),
    loadingComponent,
    next,
    skip,
    back,
    navigate,
    goToIndex,
    goToStepId,
    goToStepIndex,
    reset
  }), [back, canGoBack, canGoNext, currentStep, currentStepIndex, goToIndex, goToStepId, goToStepIndex, isComplete, isFirstStep, isLastStep, loadingComponent, navigate, next, progress, reset, skip, state.currentIndex, state.isLoading, totalSteps]);
}
export function OrchestratorProvider(_ref) {
  let {
    flow,
    children,
    loadingComponent,
    onError,
    initialIndex,
    initialStepId,
    onStepChange,
    onStepChangeDetailed,
    onNavigate
  } = _ref;
  const orchestrator = useOrchestratorFlow(flow, onError, loadingComponent, initialIndex, initialStepId, onStepChange, onStepChangeDetailed, onNavigate);
  return /*#__PURE__*/React.createElement(OrchestratorContext.Provider, {
    value: orchestrator
  }, children);
}
export function useOrchestrator() {
  const context = useContext(OrchestratorContext);
  if (!context) {
    throw new Error('useOrchestrator must be used within an OrchestratorProvider');
  }
  return context;
}
//# sourceMappingURL=context.js.map